import { Component, Input, OnInit } from '@angular/core';
import { NotificationService, NotificationChannel, NotificationChannelState } from '../../../core/services/notification.service';
import { ToastService } from '../../../core/services/toast.service';

type LoadState = 'loading' | 'loaded' | 'error';
type SaveState = 'idle' | 'saving';
type TestState = 'idle' | 'testing' | 'success' | 'error';
type RemoveState = 'idle' | 'removing';

@Component({
  selector: 'app-webhook-config-card',
  templateUrl: './webhook-config-card.component.html',
  styles: [':host { display: block; }'],
})
export class WebhookConfigCardComponent implements OnInit {
  @Input() channel!: NotificationChannel;
  @Input() label!: string;
  @Input() description!: string;
  @Input() icon!: string;
  @Input() iconBg!: string;
  @Input() iconColor!: string;
  @Input() helpText!: string;
  @Input() urlPlaceholder = 'https://';
  @Input() readOnly = false;

  config: NotificationChannelState | null = null;

  loadState: LoadState   = 'loading';
  saveState: SaveState   = 'idle';
  testState: TestState   = 'idle';
  removeState: RemoveState = 'idle';

  testMessage = '';

  showForm     = false;
  showUrlInput = false;

  draftUrl     = '';
  draftEnabled = true;
  submitAttempted = false;
  urlError: string | null = null;

  constructor(
    private readonly notification: NotificationService,
    private readonly toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadConfig();
  }

  // ── Internal load ──────────────────────────────────────────────────────────

  private loadConfig(): void {
    this.loadState = 'loading';
    this.notification.getChannel(this.channel).subscribe({
      next: (state) => {
        // A state with configured: false is treated the same as no record
        this.config    = (state && state.configured) ? state : null;
        this.loadState = 'loaded';
        if (this.config) {
          this.draftEnabled = this.config.enabled;
        }
      },
      error: () => { this.loadState = 'error'; },
    });
  }

  // ── Not-configured form ────────────────────────────────────────────────────

  startConfigure(): void {
    this.draftUrl     = '';
    this.draftEnabled = true;
    this.submitAttempted = false;
    this.urlError = null;
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.draftUrl = '';
    this.urlError = null;
    this.submitAttempted = false;
  }

  saveConfig(): void {
    this.submitAttempted = true;
    this.urlError = this.validateUrl(this.draftUrl);
    if (this.urlError) return;

    this.saveState = 'saving';
    this.notification.saveChannel(this.channel, {
      enabled:    this.draftEnabled,
      webhookUrl: this.draftUrl,
    }).subscribe({
      next: (saved) => {
        this.config    = saved;
        this.showForm  = false;
        this.saveState = 'idle';
        this.draftUrl  = '';
        this.submitAttempted = false;
        this.toast.show(`${this.label} webhook configured.`, 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.saveState = 'idle';
        this.toast.show(err?.error?.message ?? `Failed to save ${this.label} webhook. Please try again.`, 'error');
      },
    });
  }

  // ── Configured: toggle enabled ─────────────────────────────────────────────

  toggleEnabled(): void {
    if (!this.config || this.saveState === 'saving') return;
    const newEnabled = !this.config.enabled;
    this.saveState = 'saving';
    this.notification.saveChannel(this.channel, { enabled: newEnabled }).subscribe({
      next: (saved) => {
        this.config    = saved;
        this.saveState = 'idle';
        this.toast.show(`${this.label} notifications ${newEnabled ? 'enabled' : 'paused'}.`, 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.saveState = 'idle';
        this.toast.show(err?.error?.message ?? `Failed to update ${this.label}. Please try again.`, 'error');
      },
    });
  }

  // ── Configured: update URL ─────────────────────────────────────────────────

  startUpdateUrl(): void {
    this.draftUrl        = '';
    this.submitAttempted = false;
    this.urlError        = null;
    this.showUrlInput    = true;
  }

  cancelUpdateUrl(): void {
    this.showUrlInput    = false;
    this.draftUrl        = '';
    this.urlError        = null;
    this.submitAttempted = false;
  }

  saveUrl(): void {
    this.submitAttempted = true;
    this.urlError = this.validateUrl(this.draftUrl);
    if (this.urlError) return;

    this.saveState = 'saving';
    this.notification.saveChannel(this.channel, {
      enabled:    this.config?.enabled ?? true,
      webhookUrl: this.draftUrl,
    }).subscribe({
      next: (saved) => {
        this.config       = saved;
        this.showUrlInput = false;
        this.saveState    = 'idle';
        this.draftUrl     = '';
        this.submitAttempted = false;
        this.toast.show(`${this.label} webhook URL updated.`, 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.saveState = 'idle';
        this.toast.show(err?.error?.message ?? `Failed to update ${this.label} URL. Please try again.`, 'error');
      },
    });
  }

  // ── Configured: test ───────────────────────────────────────────────────────

  sendTest(): void {
    if (this.testState === 'testing') return;
    this.testState   = 'testing';
    this.testMessage = '';
    this.notification.testChannel(this.channel).subscribe({
      next: (result) => {
        this.testState   = result.success ? 'success' : 'error';
        this.testMessage = result.message;
      },
      error: (err: { error?: { message?: string } }) => {
        this.testState   = 'error';
        this.testMessage = err?.error?.message ?? `Failed to send test notification.`;
      },
    });
  }

  dismissTestResult(): void {
    this.testState   = 'idle';
    this.testMessage = '';
  }

  // ── Configured: remove ─────────────────────────────────────────────────────

  removeChannel(): void {
    if (this.removeState === 'removing') return;
    this.removeState = 'removing';
    this.notification.removeChannel(this.channel).subscribe({
      next: () => {
        this.config      = null;
        this.removeState = 'idle';
        this.testState   = 'idle';
        this.testMessage = '';
        this.toast.show(`${this.label} webhook removed.`, 'success');
      },
      error: (err: { error?: { message?: string } }) => {
        this.removeState = 'idle';
        this.toast.show(err?.error?.message ?? `Failed to remove ${this.label}. Please try again.`, 'error');
      },
    });
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  private validateUrl(url: string): string | null {
    if (!url || !url.trim()) return 'Webhook URL is required';
    if (!url.startsWith('https://')) return 'Webhook URL must start with https://';
    if (this.channel === 'slack' && !url.startsWith('https://hooks.slack.com/')) {
      return 'Slack webhook URLs must start with https://hooks.slack.com/';
    }
    return null;
  }
}
